import React, { useState } from "react";
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Linking,
} from "react-native";

import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { Ionicons } from "@expo/vector-icons";
import { useDevice, useScreenDimensions } from "../_layout";
import GlobalStyles from "../../Resources/Styles";
import { useTranslation } from "react-i18next";
import { getFormattedVersion, getAppVersion } from "../utils/version";
import { storage } from "../Firestore/FirestoreConfig";
import { ref, getDownloadURL } from "firebase/storage";

// Feature tag color mapping
const FEATURE_COLORS = {
  "Coach app": {
    border: Colors.grayColor,
    background: "rgba(156, 156, 156, 0.1)",
    text: "rgba(95, 95, 95, 1)",
  },
  General: {
    border: Colors.grayColor,
    background: "rgba(156, 156, 156, 0.1)",
    text: "rgba(95, 95, 95, 1)",
  },
  Settings: {
    border: Colors.grayColor,
    background: "rgba(156, 156, 156, 0.1)",
    text: "rgba(95, 95, 95, 1)",
  },
  "Athlete app": {
    border: Colors.blackColor,
    background: "rgba(0, 0, 0, 0.1)",
    text: "rgba(95, 95, 95, 1)",
  },
  Chat: {
    border: Colors.noteColor,
    background: "rgba(226, 193, 54, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  Forms: {
    border: Colors.tracColor,
    background: "rgba(194, 99, 249, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  Applications: {
    border: Colors.tracColor,
    background: "rgba(194, 99, 249, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  Statistics: {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  Competitions: {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  Program: {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  "Modifiers library": {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  Calendar: {
    border: "#43A047",
    background: "rgba(67, 160, 71, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  "Athlete overview": {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  "Workout library": {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  "Exercise library": {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  Block: {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
  "Block library": {
    border: Colors.bubbleColor,
    background: "rgba(63, 123, 141, 0.15)",
    text: "rgba(95, 95, 95, 1)",
  },
};

// Helper function removed - no longer needed since we define backgrounds explicitly

// Reusable FeatureBox component that can handle different content types
const FeatureBox = ({
  title,
  content,
  tags,
  cta,
  ctaIntro,
  introText,
  getTagTranslation,
}) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const [hoveredCta, setHoveredCta] = useState(false);

  const renderContent = () => {
    // If content is a string
    if (typeof content === "string") {
      return (
        <Text
          style={[
            screenSize === "small" ? Fonts.regular12 : Fonts.regular13,
            { color: Colors.grayColor, lineHeight: 18 },
          ]}
        >
          {content}
        </Text>
      );
    }

    // If content is an array (bullet points)
    if (Array.isArray(content)) {
      return (
        <View style={{ gap: 4 }}>
          {content.map((item, index) => (
            <View key={index} style={styles.bulletPoint}>
              <Text
                style={[
                  screenSize === "small" ? Fonts.regular12 : Fonts.regular13,
                  { color: Colors.grayColor },
                ]}
              >
                •
              </Text>
              <Text
                style={[
                  screenSize === "small" ? Fonts.regular12 : Fonts.regular13,
                  { color: Colors.grayColor, lineHeight: 18, flex: 1 },
                ]}
              >
                {item}
              </Text>
            </View>
          ))}
        </View>
      );
    }

    // If content is an object with text and links
    if (content && typeof content === "object" && content.parts) {
      return (
        <Text
          style={[
            screenSize === "small" ? Fonts.regular12 : Fonts.regular13,
            { color: Colors.grayColor, lineHeight: 18 },
          ]}
        >
          {content.parts.map((part, index) => {
            if (part.link) {
              return (
                <Text
                  key={index}
                  style={{
                    color: Colors.bubbleColor,
                    textDecorationLine: "underline",
                  }}
                  onPress={() => Linking.openURL(part.link)}
                >
                  {part.text}
                </Text>
              );
            }
            return <Text key={index}>{part.text}</Text>;
          })}
        </Text>
      );
    }

    // Custom content (can be any React element)
    return content;
  };

  return (
    <View style={styles.featureBox}>
      <Text
        style={[
          screenSize === "small" ? Fonts.medium13 : Fonts.medium14,
          { color: Colors.blackColor, marginBottom: 4 },
        ]}
      >
        {title}
      </Text>

      {/* Intro text before bullets */}
      {introText && (
        <Text
          style={[
            screenSize === "small" ? Fonts.regular12 : Fonts.regular13,
            { color: Colors.grayColor, lineHeight: 18, marginBottom: 6 },
          ]}
        >
          {introText}
        </Text>
      )}

      {/* Render content */}
      {renderContent()}

      {/* CTA button(s) */}
      {cta && (
        <View
          style={{
            marginTop: 8,
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          {ctaIntro && (
            <Text
              style={[
                screenSize === "small" ? Fonts.regular12 : Fonts.regular13,
                { color: Colors.grayColor },
              ]}
            >
              {ctaIntro}
            </Text>
          )}
          {Array.isArray(cta) ? (
            // Multiple CTAs
            cta.map((ctaItem, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Text
                    style={[
                      screenSize === "small"
                        ? Fonts.regular12
                        : Fonts.regular13,
                      { color: Colors.grayColor },
                    ]}
                  >
                    ·
                  </Text>
                )}
                <TouchableOpacity
                  onPress={() => ctaItem.onPress && ctaItem.onPress()}
                  onMouseEnter={() => !isMobile && setHoveredCta(index)}
                  onMouseLeave={() => setHoveredCta(false)}
                >
                  <Text
                    style={[
                      screenSize === "small" ? Fonts.medium12 : Fonts.medium13,
                      {
                        color: Colors.bubbleColor,
                        opacity: hoveredCta === index ? 0.7 : 1,
                      },
                    ]}
                  >
                    {ctaItem.text}
                  </Text>
                </TouchableOpacity>
              </React.Fragment>
            ))
          ) : (
            // Single CTA
            <TouchableOpacity
              onPress={() => cta.onPress && cta.onPress()}
              onMouseEnter={() => !isMobile && setHoveredCta(true)}
              onMouseLeave={() => setHoveredCta(false)}
            >
              <Text
                style={[
                  screenSize === "small" ? Fonts.medium12 : Fonts.medium13,
                  {
                    color: Colors.bubbleColor,
                    opacity: hoveredCta ? 0.7 : 1,
                  },
                ]}
              >
                {cta.text}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Tags */}
      {tags && tags.length > 0 && (
        <View style={styles.tagsContainer}>
          {tags.map((tag, index) => {
            const colorConfig = FEATURE_COLORS[tag] || {
              border: Colors.lightGrayColor,
              background: Colors.lightGrayColor,
              text: Colors.blackColor,
            };
            return (
              <View
                key={index}
                style={[
                  styles.tag,
                  {
                    borderColor: colorConfig.border,
                    backgroundColor: colorConfig.background,
                  },
                ]}
              >
                <Text style={[styles.tagText, { color: colorConfig.text }]}>
                  {getTagTranslation ? getTagTranslation(tag) : tag}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

// Reusable ReleaseSection component
const ReleaseSection = ({
  version,
  date,
  features,
  getTagTranslation,
  formatDate,
}) => {
  const { screenSize } = useScreenDimensions();
  return (
    <View style={styles.releaseSection}>
      <View style={styles.releaseSectionHeader}>
        <Text
          style={[
            screenSize === "small" ? Fonts.medium13 : Fonts.medium14,
            { color: Colors.grayColor },
          ]}
        >
          {version}
        </Text>
        <Text
          style={[
            {
              color: Colors.grayColor,
              fontSize: screenSize === "small" ? 11 : 12,
            },
          ]}
        >
          {formatDate ? formatDate(date) : date}
        </Text>
      </View>

      <View style={{ gap: 12 }}>
        {features.map((feature, index) => (
          <FeatureBox
            key={index}
            title={feature.title}
            content={feature.content}
            tags={feature.tags}
            cta={feature.cta}
            ctaIntro={feature.ctaIntro}
            introText={feature.introText}
            getTagTranslation={getTagTranslation}
          />
        ))}
      </View>
    </View>
  );
};

const WhatsNewModal = ({
  isVisible,
  onClose,
  onSendFeedback,
  onPrimaryPress,
}) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const [hoveredButton, setHoveredButton] = useState(null);
  const { i18n, t } = useTranslation();

  // Get version from app config or use prop
  const displayVersion = getFormattedVersion();

  // Helper function to get localized tag name
  const getTagTranslation = (tagKey) => {
    const tagMap = {
      General: "tagGeneral-string",
      "Coach app": "tagCoachApp-string",
      "Athlete app": "tagAthleteApp-string",
      Chat: "tagChat-string",
      Settings: "tagSettings-string",
      Forms: "tagForms-string",
      Applications: "tagApplications-string",
      Statistics: "tagStatistics-string",
      Competitions: "tagCompetitions-string",
      Program: "tagProgram-string",
      "Modifiers library": "tagModifiersLibrary-string",
      Calendar: "tagCalendar-string",
      "Athlete overview": "tagAthleteOverview-string",
      "Workout library": "tagWorkoutLibrary-string",
      "Exercise library": "tagExerciseLibrary-string",
      Block: "tagBlock-string",
      "Block library": "tagBlockLibrary-string",
    };
    return t(tagMap[tagKey] || tagKey);
  };

  // Helper function to format dates according to language
  const formatDate = (dateString) => {
    // dateString format: "YYYY-MM-DD"
    const [year, month, day] = dateString.split("-");
    const monthNames = [
      "monthJan-string",
      "monthFeb-string",
      "monthMar-string",
      "monthApr-string",
      "monthMay-string",
      "monthJun-string",
      "monthJul-string",
      "monthAug-string",
      "monthSep-string",
      "monthOct-string",
      "monthNov-string",
      "monthDec-string",
    ];
    const monthIndex = parseInt(month) - 1;
    const translatedMonth = t(monthNames[monthIndex]);
    return `${parseInt(day)} ${translatedMonth} ${year}`;
  };

  const downloadAthleteGuide = async (language) => {
    try {
      const pdfRef = ref(
        storage,
        language === "es"
          ? "documents/guia_uso_efort_app.pdf"
          : "documents/user_guide_efort_app.pdf"
      );
      const url = await getDownloadURL(pdfRef);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  // Release data structure
  const releases = [
    {
      version: "v2.0.0",
      date: "2026-02-03",
      features: [
        {
          title: t("v200WorkoutsViewRedesign-string"),
          introText: t("v200WorkoutsViewRedesignIntro-string"),
          content: [
            t("v200WorkoutsViewRedesignBullet1-string"),
            t("v200WorkoutsViewRedesignBullet2-string"),
            t("v200WorkoutsViewRedesignBullet3-string"),
            t("v200WorkoutsViewRedesignBullet4-string"),
            t("v200WorkoutsViewRedesignBullet5-string"),
          ],
          tags: ["Program"],
        },
      ],
    },
    {
      version: "v1.0.30",
      date: "2026-01-30",
      features: [
        {
          title: t("v130SmallProgramImprovements-string"),
          content: t("v130SmallProgramImprovementsDesc-string"),
          tags: ["Program"],
        },
        {
          title: t("v130ChatMessageTimeFix-string"),
          content: t("v130ChatMessageTimeFixDesc-string"),
          tags: ["Chat"],
        },
      ],
    },
    {
      version: "v1.29.0",
      date: "2026-01-11",
      features: [
        {
          title: t("v129ExerciseSearchbar-string"),
          content: t("v129ExerciseSearchbarDesc-string"),
          tags: ["Program"],
        },
        {
          title: t("v129StatisticsImprovements-string"),
          content: t("v129StatisticsImprovementsDesc-string"),
          tags: ["Statistics"],
        },
        {
          title: t("v129ApplicationsIndicator-string"),
          content: t("v129ApplicationsIndicatorDesc-string"),
          tags: ["Applications"],
        },
      ],
    },
    {
      version: "v1.28.0",
      date: "2025-12-26",
      features: [
        {
          title: t("v128SimplifiedModifiers-string"),
          content: t("v128SimplifiedModifiersDesc-string"),
          tags: ["Program"],
        },
        {
          title: t("v128ModifiersLibrary-string"),
          content: t("v128ModifiersLibraryDesc-string"),
          tags: ["Modifiers library"],
        },
        {
          title: t("v128ProgramImprovements-string"),
          content: t("v128ProgramImprovementsDesc-string"),
          tags: ["Program"],
        },
      ],
    },
    {
      version: "v1.27.0",
      date: "2025-12-14",
      features: [
        {
          title: t("v127SmallUIImprovements-string"),
          content: t("v127SmallUIImprovementsDesc-string"),
          tags: ["General", "Program"],
        },
        {
          title: t("v127OpenAthleteFromChat-string"),
          content: t("v127OpenAthleteFromChatDesc-string"),
          tags: ["Chat"],
        },
        {
          title: t("v127OpenBlockFromCalendar-string"),
          content: t("v127OpenBlockFromCalendarDesc-string"),
          tags: ["Calendar"],
        },
      ],
    },
    {
      version: "v1.26.0",
      date: "2025-12-05",
      features: [
        {
          title: t("v126AthleteOverviewRedesign-string"),
          content: t("v126AthleteOverviewRedesignDesc-string"),
          tags: ["Athlete overview"],
        },
        {
          title: t("v126FormsNotifications-string"),
          content: t("v126FormsNotificationsDesc-string"),
          tags: ["Forms"],
        },
        {
          title: t("v126ActiveBlockAndWeeks-string"),
          content: t("v126ActiveBlockAndWeeksDesc-string"),
          tags: ["Program"],
        },
      ],
    },
    {
      version: "v1.25.0",
      date: "2025-11-29",
      features: [
        {
          title: t("v125InviteMultipleAthletes-string"),
          content: t("v125InviteMultipleAthletesDesc-string"),
          tags: ["General"],
          ctaIntro: t("v125DownloadAthleteGuideIntro-string"),
          cta: [
            {
              text: "ES",
              onPress: () => downloadAthleteGuide("es"),
            },
            {
              text: "EN",
              onPress: () => downloadAthleteGuide("en"),
            },
          ],
        },
        {
          title: t("v125DefaultBlockView-string"),
          content: t("v125DefaultBlockViewDesc-string"),
          tags: ["Settings", "Program"],
        },
        {
          title: t("v125UpdatedDashboardDesign-string"),
          content: t("v125UpdatedDashboardDesignDesc-string"),
          tags: ["General"],
        },
      ],
    },
    {
      version: "v1.24.0",
      date: "2025-11-24",
      features: [
        {
          title: t("v124NewOptionsInSettings-string"),
          introText: t("v124NewOptionsIntro-string"),
          content: [
            t("v124ShowExerciseStats-string"),
            t("v124ShowSetPriority-string"),
          ],
          tags: ["Settings", "Statistics", "Program"],
        },
        {
          title: t("v124CustomizableSidebar-string"),
          content: t("v124CustomizableSidebarDesc-string"),
          tags: ["General"],
        },
        {
          title: t("v124ToastNotification-string"),
          content: t("v124ToastNotificationDesc-string"),
          tags: ["General"],
        },
        {
          title: t("v124PersonalizedOnboarding-string"),
          content: t("v124PersonalizedOnboardingDesc-string"),
          tags: ["General"],
        },

        {
          title: t("v124UpdatedLoginSignup-string"),
          content: t("v124UpdatedLoginSignupDesc-string"),
          tags: ["General"],
        },
      ],
    },
    {
      version: "v1.23.0",
      date: "2025-11-04",
      features: [
        {
          title: t("v123CoachAppStores-string"),
          content: {
            parts: [
              {
                text: t("v123CoachAppStoresDesc1-string"),
              },
              {
                text: t("v123CoachAppStoresAppStore-string"),
                link: "https://apps.apple.com/us/app/efort-coach/id6747997566",
              },
              {
                text: t("v123CoachAppStoresDesc2-string"),
              },
              {
                text: t("v123CoachAppStoresGooglePlay-string"),
                link: "https://play.google.com/store/apps/details?id=com.EfortCoach.EfortCoachApp&hl=en",
              },
              {
                text: t("v123CoachAppStoresDesc3-string"),
              },
            ],
          },
          tags: ["Coach app"],
        },
      ],
    },
    {
      version: "v1.22.0",
      date: "2025-11-02",
      features: [
        {
          title: t("v122NewTutorialsSite-string"),
          content: t("v122NewTutorialsSiteDesc-string"),
          tags: ["General"],
          cta: {
            text: t("accessTutorials-string"),
            onPress: () => {
              const isSpanish = i18n.language === "es";
              const url = isSpanish
                ? "https://app.efortcoach.com/docs/es"
                : "https://app.efortcoach.com/docs/";
              Linking.openURL(url);
            },
          },
        },
      ],
    },
    {
      version: "v1.21.0",
      date: "2025-10-28",
      features: [
        {
          title: t("v121MarkChatsUnread-string"),
          content: t("v121MarkChatsUnreadDesc-string"),
          tags: ["Chat"],
        },
        {
          title: t("v121NewVideoPlayer-string"),
          content: t("v121NewVideoPlayerDesc-string"),
          tags: ["Chat"],
        },
      ],
    },
    {
      version: "v1.20.0",
      date: "2025-09-09",
      features: [
        {
          title: t("v120ExerciseLibrary-string"),
          content: t("v120ExerciseLibraryDesc-string"),
          tags: ["Exercise library"],
        },
      ],
    },
    {
      version: "v1.19.0",
      date: "2025-08-22",
      features: [
        {
          title: t("v119NewCalendar-string"),
          content: t("v119NewCalendarDesc-string"),
          tags: ["Calendar"],
        },
      ],
    },
    {
      version: "v1.18.0",
      date: "2025-07-26",
      features: [
        {
          title: t("v118NewDashboard-string"),
          content: t("v118NewDashboardDesc-string"),
          tags: ["Program"],
        },
        {
          title: t("v118SetIntervals-string"),
          content: t("v118SetIntervalsDesc-string"),
          tags: ["Program"],
        },
      ],
    },
    {
      version: "v1.17.0",
      date: "2025-05-03",
      features: [
        {
          title: t("v117ChatAndVideos-string"),
          content: t("v117ChatAndVideosDesc-string"),
          tags: ["Chat"],
        },
      ],
    },
  ];

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={onClose}
    >
      <View style={GlobalStyles.centeredView}>
        <TouchableOpacity
          onPress={onClose}
          style={GlobalStyles.touchableBackground}
        >
          <View />
        </TouchableOpacity>
        <View
          style={{
            position: "absolute",
            maxWidth: "90%",
            maxHeight: "95%",
            // width: "90%",
            // height: "95%",
            justifyContent: "center",
          }}
          onStartShouldSetResponder={() => false}
        >
          <View
            style={[
              styles.modalView,
              {
                alignSelf: "center",
                width: "100%",
                height: "100%",
                maxHeight: 700,
                maxWidth: 900,
                flexShrink: 1,
              },
            ]}
          >
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerContent}>
                <Text
                  style={[
                    screenSize === "small" ? Fonts.medium18 : Fonts.medium20,
                    { color: Colors.blackColor, marginBottom: 4 },
                  ]}
                >
                  {t("whatsNew-string")}
                </Text>
                <Text
                  style={[
                    screenSize === "small" ? Fonts.regular13 : Fonts.regular14,
                    {
                      color: Colors.grayColor,
                      lineHeight: 20,
                      maxWidth: 600,
                    },
                  ]}
                >
                  {t("productUpdatesForEfortCoach-string")}
                </Text>
                <View style={styles.versionBadge}>
                  <Text style={styles.versionBadgeText}>
                    {t("currentVersion-string", {
                      version: displayVersion,
                    })}
                  </Text>
                </View>
              </View>

              {/* Close button */}
              <TouchableOpacity
                style={styles.closeButton}
                onPress={onClose}
                onMouseEnter={() => !isMobile && setHoveredButton("close")}
                onMouseLeave={() => setHoveredButton(null)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name="close"
                  size={24}
                  color={
                    hoveredButton === "close"
                      ? Colors.blackColor
                      : Colors.grayColor
                  }
                />
              </TouchableOpacity>
            </View>

            {/* Scrollable content */}
            <ScrollView
              style={styles.scrollView}
              contentContainerStyle={styles.scrollViewContent}
              showsVerticalScrollIndicator={true}
            >
              {releases.map((release, index) => (
                <ReleaseSection
                  key={index}
                  version={release.version}
                  date={release.date}
                  features={release.features}
                  getTagTranslation={getTagTranslation}
                  formatDate={formatDate}
                />
              ))}

              {/* Older updates hint */}
              <View style={styles.olderUpdatesHint}>
                <View style={styles.hintLine} />
                <Text style={styles.hintText}>{t("olderUpdates-string")}</Text>
                <View style={styles.hintLine} />
              </View>
            </ScrollView>

            {/* Footer */}
            <View
              style={[
                styles.footer,
                {
                  flexDirection: screenSize === "small" ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                },
              ]}
            >
              <View style={styles.footerContent}>
                <Text
                  style={[
                    screenSize === "small" ? Fonts.medium12 : Fonts.medium14,
                    {
                      color: Colors.blackColor,
                      marginBottom: 4,
                      textAlign: screenSize === "small" ? "center" : "left",
                    },
                  ]}
                >
                  {t("wantToRequestFeature-string")}
                </Text>
                <Text
                  style={[
                    screenSize === "small" ? Fonts.regular12 : Fonts.regular13,
                    {
                      color: Colors.grayColor,
                      textAlign: screenSize === "small" ? "center" : "left",
                    },
                  ]}
                >
                  {t("sendUsYourIdeas-string")}
                </Text>
              </View>

              <View style={styles.footerButtons}>
                <TouchableOpacity
                  style={[
                    styles.secondaryButton,
                    hoveredButton === "feedback" &&
                      styles.secondaryButtonHovered,
                  ]}
                  onPress={onSendFeedback}
                  onMouseEnter={() => !isMobile && setHoveredButton("feedback")}
                  onMouseLeave={() => setHoveredButton(null)}
                >
                  <Text style={styles.secondaryButtonText}>
                    {t("sendFeedbackLowercased-string")}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.primaryButton,
                    hoveredButton === "gotit" && styles.primaryButtonHovered,
                  ]}
                  onPress={onPrimaryPress}
                  onMouseEnter={() => !isMobile && setHoveredButton("gotit")}
                  onMouseLeave={() => setHoveredButton(null)}
                >
                  <Text style={styles.primaryButtonText}>
                    {t("gotIt-string")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.lightGrayColor,
    position: "relative",
  },

  headerContent: {
    flex: 1,
    paddingRight: 40,
  },

  versionBadge: {
    alignSelf: "flex-start",
    marginTop: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.1)",
  },

  versionBadgeText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.bubbleColor,
  },

  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    padding: 8,
    borderColor: Colors.lightGrayColor,
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
  },

  scrollView: {
    flex: 1,
    paddingHorizontal: 24,
  },

  scrollViewContent: {
    paddingTop: 12,
    paddingBottom: 16,
    gap: 24,
  },

  releaseSection: {
    gap: 12,
  },

  releaseSectionHeader: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
  },

  featureBox: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.lightGrayColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 16,
    paddingVertical: 12,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },

  bulletPoint: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },

  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },

  tag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
  },

  tagText: {
    fontSize: 10,
    fontWeight: "500",
  },

  olderUpdatesHint: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 8,
  },

  hintLine: {
    width: 24,
    height: 1,
    backgroundColor: Colors.lightGrayColor,
  },

  hintText: {
    fontSize: 10,
    color: Colors.grayColor,
  },

  footer: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.lightGrayColor,
    backgroundColor: "#FAFAFA",
    gap: 12,
  },

  footerContent: {
    flexShrink: 1,
  },

  footerButtons: {
    flexDirection: "row",
    gap: 8,
    alignSelf: "center",
  },

  primaryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 14,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },

  primaryButtonHovered: {
    opacity: 0.85,
  },

  primaryButtonText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.whiteColor,
  },

  secondaryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.whiteColor,
    alignItems: "center",
    justifyContent: "center",
  },

  secondaryButtonHovered: {
    backgroundColor: "rgba(0, 122, 255, 0.05)",
  },

  secondaryButtonText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
});

export default WhatsNewModal;
